import React, { useEffect } from "react";
import { Prompt } from "react-router-dom";
import { TextField, Switch, Box, Grid, Backdrop, Button } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, Theme } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import { object, string } from "yup";
import {
  getAllYardAction,
  addNewYardRow,
  gatAllYardRowAction,
  updateYardRow,
} from "src/app-redux/configuration/actions/configurationActions";
import {
  selectAllYardsList,
  selectAllYardRowsList,
  loader,
  setisDirtyValue,
} from "src/app-redux/configuration/configurationSlice";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import { IAddYardRow } from "src/common/typings/configuration";
import { IVehicleType } from "src/common/typings/vehicle";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import CommonAutoCompleteField from "../../../../../../components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonButton from "../../../../../../components/CommonButton/CommonButton";
import { columns } from "./columns";
import { Check, Close } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonLoader from "src/components/Loader/CommonLoader";

const useStyles = makeStyles((theme: Theme) => ({
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },

  UserMainPanel: {
    width: "100%",
    float: "left",
    padding: "0px 20px",
    paddingRight: 0,
  },
  mainConainer: { marginTop: "0px" },
}));

export interface IForm extends IVehicleType {}

interface Props {}

export function InputFields({}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [isEditMode, setisEditMode] = React.useState<boolean>(false);

  const loading = useAppSelector(loader);
  const allYards = useAppSelector(selectAllYardsList);
  const allYardRows = useAppSelector(selectAllYardRowsList);

  // Form Submission
  const validationSchema = object({
    noOfVehicles: string()
      .required("This field is required")
      .max(10, "Maximum 10 digits are allowed."),
    yardId: string().required("This field is required"),
  });
  const formik = useFormik<IAddYardRow>({
    enableReinitialize: true,
    initialValues: {
      yardRowId: "",
      yardId: "",
      noOfVehicles: "",
      isActive: false,
      rowName: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, actions) => {
      let res;
      if (isEditMode) res = await dispatch(updateYardRow(values));
      else res = await dispatch(addNewYardRow(values));
      if (res?.payload) {
        dispatch(gatAllYardRowAction(false));
        actions.setSubmitting(false);
        actions.resetForm();
        setisEditMode(false);
        setIsDirtyValue(false);
      }
    },
  });
  // End Form Submission
  const handleUpdateClick = (params: any) => {
    setisEditMode(true);
    formik.setFieldValue("yarId", params.id);
    formik.setFieldValue("yardId", params.yardId);
    formik.setFieldValue("noOfVehicles", params.noOfVehicles);
    formik.setFieldValue("rowName", params.rowName);
    formik.setFieldValue("isActive", params.isActive);
    formik.setFieldValue("yardRowId", params.yardRowId);
  };
  useEffect(() => {
    dispatch(getAllYardAction(false));
    dispatch(gatAllYardRowAction(false));
  }, []);

  /* Transform Column */
  useEffect(() => {
    if (allYardRows.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        setIsDirtyValue(true);
                        handleUpdateClick(row.original);
                      }}
                      icon={<EditIcon />}
                      isEdit={true}
                    >
                      Edit
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column.accessorKey === "rowName"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return <>{`Row ${row.original.rowName}`}</>;
              },
            }
          : column.accessorKey === "isActive"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isActive ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isActive ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [allYardRows]);

  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  useEffect(() => {
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);
  function handleChange(e: any) {
    formik.handleChange(e);
    e.target.value || JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues)
      ? setIsDirtyValue(true)
      : setIsDirtyValue(false);
  }

  function setIsDirtyValue(val: boolean) {
    setIsDirty(val);
    dispatch(setisDirtyValue(val));
  }

  return (
    <>
      {/* If prompt, when navigating to another page inside the app */}
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          {/* First Section */}
          <>
            <Grid className={classes.UserMainPanel}>
              <Grid
                // Container
                container
                className={classes.mainConainer}
                direction="row"
                alignItems="center"
                spacing={2}
              >
                <Grid container item xs={12} lg={9} spacing={2}>
                  <Grid item xs={12} md={4} style={{ paddingLeft: "0px", paddingRight: 0 }}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        disabled={isEditMode}
                        label="Yard Name"
                        placeholder="Select Yard Name"
                        name="yardId"
                        optionsType={allYards}
                        getOptionLabel={(option) => option.name}
                        required
                        value={allYards.find((st: any) => st.id === formik.values.yardId) || null}
                        onChange={(option: any) => {
                          if (option && option.name !== "" && option.id !== 0) {
                            formik.setFieldValue("yardId", option.id);
                            handleChange({ target: { value: option.id } });
                          } else {
                            handleChange({ target: { value: null } });
                          }
                        }}
                        error={formik.touched.yardId && Boolean(formik.errors.yardId)}
                        helperText={formik.touched.yardId && formik.errors.yardId}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} style={{ paddingLeft: "0px", paddingRight: 0 }}>
                    <Box sx={{ marginLeft: { xs: 0, md: "8px" } }}>
                      <TextField
                        className="customTextField"
                        type="number"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="No. of Vehicles in a Row"
                        name="noOfVehicles"
                        placeholder="Enter No. of Vehicles in a Row"
                        disabled={false}
                        value={formik.values.noOfVehicles}
                        onChange={handleChange}
                        error={formik.touched.noOfVehicles && Boolean(formik.errors.noOfVehicles)}
                        helperText={formik.touched.noOfVehicles && formik.errors.noOfVehicles}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2} style={{ paddingLeft: "12px" }}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(formik.values.isActive)}
                            onChange={formik.handleChange}
                            name="isActive"
                            color="primary"
                          />
                        }
                        label="Active"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2} style={{ paddingRight: 0 }}>
                    <Box mb={1} mt={1} className={"headerButtonsConfig"} style={{ float: "right" }}>
                      <CommonButton
                        isGreen
                        cssClass="customSubmitBtn"
                        onClick={() => {
                          formik.submitForm();
                        }}
                      >
                        {isEditMode ? "Update" : "Add"}
                      </CommonButton>
                    </Box>
                  </Grid>

                  <Grid md={12}>
                    <CommonMaterialGrid
                      defaultPerPageRecord={10}
                      height={400}
                      rows={allYardRows || []}
                      columns={displayColumns}
                    />
                  </Grid>
                </Grid>
                {/* End Inner Container */}
              </Grid>
              {/* End Outer Container */}
            </Grid>
          </>
        </Grid>
      </form>
    </>
  );
}
